import { Breadcrumbs } from '@warp-ds/react/components/breadcrumbs';
import { Button } from '@warp-ds/react/components/button';
import { Modal } from '@warp-ds/react/components/modal';
import { type PropsWithChildren, type ReactElement, useState } from 'react';
import { thousandSeparated } from '../../util/formatting';
import { SortDetails } from './SortDetails';

type BreadcrumbItem = {
    label: string;
    href?: string;
};

type BreadcrumbConfig = {
    path: string;
    title?: string;
    items?: BreadcrumbItem[];
};

type ResultPageLayoutProps = {
    title: string;
    visibleTitle?: string;
    breadcrumbs: BreadcrumbConfig;
    filters: ReactElement;
    search?: ReactElement;
    sortingSection: string;
    sorting?: ReactElement;
    saveSearch?: ReactElement;
    numberOfOffers?: number;
    mapUrl?: string;
    customTopBar?: boolean; // Flight does not have a sorting component and needs some custom top bar structuring.
};

export const ResultPageLayout = ({
    title,
    visibleTitle,
    breadcrumbs,
    search,
    filters,
    sortingSection,
    sorting,
    saveSearch,
    numberOfOffers,
    mapUrl,
    children,
    customTopBar = false,
}: PropsWithChildren<ResultPageLayoutProps>) => {
    const [showFilters, setShowFilters] = useState<boolean>(false);
    const { items } = breadcrumbs;

    return (
        <>
            <Breadcrumbs className="hidden md:block pt-16 my-16">
                <a href="/reise/">Reise</a>
                <a href={`/reise/${breadcrumbs.path}/`} data-testid="submarket">
                    {title}
                </a>
                {items ? (
                    items.map((item, index) => {
                        const isLast = index === items.length - 1;
                        return item.href ? (
                            <a key={`${index}-${item.label}`} href={item.href}>
                                {item.label}
                            </a>
                        ) : (
                            <span key={`${index}-${item.label}`} aria-current={isLast ? 'page' : undefined}>
                                {item.label}
                            </span>
                        );
                    })
                ) : (
                    <span aria-current="page">{breadcrumbs.title ?? 'Resultat'}</span>
                )}
            </Breadcrumbs>
            <h1 className={visibleTitle ? 't3 mb-16' : 'sr-only'}>{visibleTitle ?? title}</h1>
            {search && (
                <section className="s-border border-b md:border md:rounded-8 my-16 pb-8 md:pb-0" data-testid="searchPanel">
                    {search}
                </section>
            )}
            <div className="md:grid md:grid-cols-3 md:gap-32 pb-32" data-testid="resultPage">
                <section className="hidden md:block md:col-span-1 divide-y-1" data-testid="filters">
                    {filters}
                </section>
                <div className="md:col-span-2">
                    <section
                        className={`flex items-center justify-between gap-16 my-16 md:mt-0 ${customTopBar ? 'md:hidden' : ''}`}
                        data-testid="topBar">
                        <div className={`flex items-center gap-8 sm:gap-16 ${saveSearch ? '' : 'md:hidden'}`}>
                            <Button
                                primary={showFilters}
                                utility={!showFilters}
                                small
                                onClick={() => setShowFilters(!showFilters)}
                                aria-label={showFilters ? 'Vis resultat' : 'Vis filter'}
                                className="flex items-center gap-8 md:hidden whitespace-nowrap"
                                data-testid="showFilterButton">
                                <w-icon name="Filter" size="small" aria-hidden />
                                Filter
                            </Button>
                            {mapUrl && (
                                <Button
                                    utility
                                    small
                                    href={mapUrl}
                                    className="flex items-center gap-8 md:hidden whitespace-nowrap"
                                    data-testid="openMapButton"
                                    aria-label="Vis i kart">
                                    <w-icon name="Map" size="small" aria-hidden />
                                    <span className="hidden sm:inline">Vis i kart</span>
                                </Button>
                            )}
                            {customTopBar ? null : saveSearch}
                        </div>

                        {numberOfOffers !== undefined && numberOfOffers !== null && (
                            <output className="whitespace-nowrap text-s md:text-m hidden sm:block" data-testid="resultCount">
                                <span className="font-bold">{numberOfOffers ? thousandSeparated(numberOfOffers) : '0'}</span> treff
                            </output>
                        )}
                        {customTopBar ? (
                            saveSearch
                        ) : (
                            <>
                                <SortDetails section={sortingSection} className="hidden md:flex" />
                                {sorting}
                            </>
                        )}
                    </section>
                    {!customTopBar && <SortDetails section={sortingSection} className="md:hidden my-16" />}
                    {children}
                </div>
            </div>
            <Modal title="Endre filter" open={showFilters} right onDismiss={() => setShowFilters(false)}>
                {filters}
            </Modal>
        </>
    );
};
